import React, { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useAuth } from '../contexts/AuthContext';
import { signInWithEmailAndPassword } from 'firebase/auth';
import { auth, managementDb } from '../firebase';
import { ref, push, serverTimestamp, get, query, orderByChild, equalTo } from 'firebase/database';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import {
  faHome,
  faTools,
  faImages,
  faEnvelope,
  faPhone,
  faMapMarkerAlt,
  faClock,
  faArrowRight,
  faUser,
  faLock,
  faEye,
  faEyeSlash
} from '@fortawesome/free-solid-svg-icons';
import {
  faInstagram,
  faLinkedinIn,
  faYoutube
} from '@fortawesome/free-brands-svg-icons';
import logo from '../assets/logos/WHITE Logo Text PNG.png';
import './Footer.css';

const Footer = () => {
  const [showLogin, setShowLogin] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [subscribeEmail, setSubscribeEmail] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { currentUser, isAdmin, isEmployee, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const isAuthenticated = !!currentUser;

  const handleClick = (e) => {
    // Only animate, do not prevent default or navigate for <Link>
    const link = e.currentTarget;
    link.classList.add('animate');
    setTimeout(() => {
      link.classList.remove('animate');
    }, 300);
  };

  const handleLogoClick = (e) => {
    handleClick(e); // reuse animation
    if (location.pathname === '/') {
      e.preventDefault();
      window.scrollTo({
        top: 0,
        behavior: 'smooth',
      });
    }
  };

  const toggleLogin = () => {
    setShowLogin(!showLogin);
    setError('');
    setShowPassword(false);
  };

  const handleSignOut = async () => {
    try {
      await logout();
      toast.success('Signed out successfully');
      navigate('/');
    } catch (err) {
      console.error('Sign out error:', err);
      toast.error('Failed to sign out');
    }
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const handleLoginSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      await signInWithEmailAndPassword(auth, email, password);
      toast.success('Login successful!');
      setShowLogin(false);
      const from = location.state?.from?.pathname || '/dashboard';
      navigate(from, { replace: true });
    } catch (err) {
      setError('Invalid Email ID/Password');
      toast.error('Invalid Email ID/Password');
    } finally {
      setLoading(false);
    }
  };

  const handleSubscribeSubmit = async (e) => {
    e.preventDefault();
    if (!subscribeEmail) {
      toast.error('Please enter your email address.');
      return;
    }

    try {
      const subscribeRef = ref(managementDb, 'subscribe');
      const emailQuery = query(subscribeRef, orderByChild('email'), equalTo(subscribeEmail));
      const snapshot = await get(emailQuery);

      if (snapshot.exists()) {
        toast.info('You are already subscribed!');
      } else {
        await push(subscribeRef, {
          email: subscribeEmail,
          timestamp: serverTimestamp(),
        });
        toast.success('Thank you for subscribing!');
        setSubscribeEmail('');
      }
    } catch (error) {
      toast.error('Subscription failed. Please try again later.');
      console.error('Subscription error:', error);
    }
  };

  return (
    <footer className="footer-container">
      {/* Login Modal */}
      {showLogin && (
        <div className="login-modal-overlay">
          <div className="login-modal-card">
            <div className="login-modal-aside">
              <img src={logo} alt="The Video Company" />
              <p>Secure access for the TVC management team.</p>
              <div className="login-meta">
                <span>Need help? <a href="mailto:connect@thevideocompany.in">Email support</a></span>
              </div>
            </div>
            <div className="login-modal-form">
              <button
                type="button"
                className="login-modal-close"
                onClick={toggleLogin}
                aria-label="Close admin login"
              >
                &times;
              </button>
              <p className="login-eyebrow">Team access</p>
              <h3 className="login-title">Admin Console</h3>
              <p className="login-subtitle">Sign in to update site content and workflows.</p>
              {error && <div className="login-error">{error}</div>}
              <form onSubmit={handleLoginSubmit}>
                <div className="form-group">
                  <label htmlFor="login-email">Email</label>
                  <div className="input-with-icon">
                    <FontAwesomeIcon icon={faUser} className="input-icon" />
                    <input
                      id="login-email"
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="you@thevideocompany.in"
                      required
                    />
                  </div>
                </div>
                <div className="form-group">
                  <label htmlFor="login-password">Password</label>
                  <div className="input-with-icon">
                    <FontAwesomeIcon icon={faLock} className="input-icon" />
                    <input
                      id="login-password"
                      type={showPassword ? "text" : "password"}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder="Enter your password"
                      required
                    />
                    <button
                      type="button"
                      className="password-toggle-btn"
                      onClick={togglePasswordVisibility}
                    >
                      <FontAwesomeIcon icon={showPassword ? faEyeSlash : faEye} />
                    </button>
                  </div>
                </div>
                <div className="login-actions">
                  <button 
                    type="submit" 
                    className="login-button"
                    disabled={loading}
                  >
                    {loading ? 'Signing in…' : 'Sign in'}
                  </button>
                  <button 
                    type="button" 
                    className="cancel-button ghost" 
                    onClick={toggleLogin}
                    disabled={loading}
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      <div className="container-fluid px-0">
        <div className="container pt-5 pb-3">
          <div className="row g-4">
            {/* Brand Column */}
            <div className="col-lg-3 col-md-6 mb-4 mb-lg-0">
              <div className="footer-brand">
                <Link to="/" className="d-block mb-3" onClick={handleLogoClick}>
                  <img 
                    src={logo} 
                    alt="Company Logo" 
                    className="img-fluid footer-logo"
                  />
                </Link>
                
                <div className="social-icons">
                  <a href="https://www.instagram.com/thevideocompany.in/" target="_blank" rel="noopener noreferrer" aria-label="Instagram" onClick={handleClick}>
                    <FontAwesomeIcon icon={faInstagram} />
                  </a>
                  <a href="https://www.linkedin.com/company/thevideocompanydotin/about/" target="_blank" rel="noopener noreferrer" aria-label="LinkedIn" onClick={handleClick}>
                    <FontAwesomeIcon icon={faLinkedinIn} />
                  </a>
                  <a href="https://youtube.com/" target="_blank" rel="noopener noreferrer" aria-label="YouTube" onClick={handleClick}>
                    <FontAwesomeIcon icon={faYoutube} />
                  </a>
                </div>
              </div>
            </div>

            {/* Quick Links Column */}
            <div className="col-lg-2 col-md-6 mb-4 mb-lg-0">
              <h5 className="footer-heading">Quick Links</h5>
              <ul className="footer-links">
                <li>
                  <Link to="/" onClick={handleClick}>
                    <FontAwesomeIcon icon={faHome} /> Home
                  </Link>
                </li>
                {/* <li>
                  <Link to="/about" onClick={handleClick}>
                    <FontAwesomeIcon icon={faInfoCircle} /> About
                  </Link>
                </li> */}
                <li>
                  <Link to="/services" onClick={handleClick}>
                    <FontAwesomeIcon icon={faTools} /> Services
                  </Link>
                </li>
                <li>
                  <Link to="/gallery" onClick={handleClick}>
                    <FontAwesomeIcon icon={faImages} /> Gallery
                  </Link>
                </li>
                <li>
                  <Link to="/contact" onClick={handleClick}>
                    <FontAwesomeIcon icon={faEnvelope} /> Contact
                  </Link>
                </li>
                <li>
                  <Link to="/join-us" onClick={handleClick} className="footer-link-btn">
                    <button style={{background: 'none', border: 'none', color: '#adb5bd', font: 'inherit', fontSize: '0.9rem', fontWeight: 400, lineHeight: 1.6, cursor: 'pointer', textAlign: 'left', display: 'flex', alignItems: 'center', gap: '0.5rem', textDecoration: 'none', transition: 'color 0.3s ease'}}>
                      <FontAwesomeIcon icon={faUser} /> Join Us
                    </button>
                  </Link>
                </li>
              </ul>
            </div>

            {/* Services Column */}
            <div className="col-lg-2 col-md-6 mb-4 mb-lg-0">
              <h5 className="footer-heading">Services</h5>
              <ul className="footer-links">
                <li>
                  <button type="button" className="footer-link-btn" onClick={() => navigate('/services?service=video-production')}>
                    <FontAwesomeIcon icon={faArrowRight} /> Video Production
                  </button>
                </li>
                <li>
                  <button type="button" className="footer-link-btn" onClick={() => navigate('/services?service=commercial-filming')}>
                    <FontAwesomeIcon icon={faArrowRight} /> Commercials
                  </button>
                </li>
                <li>
                  <button type="button" className="footer-link-btn" onClick={() => navigate('/services?service=corporate-videos')}>
                    <FontAwesomeIcon icon={faArrowRight} /> Corporate Videos
                  </button>
                </li>
                <li>
                  <button type="button" className="footer-link-btn" onClick={() => navigate('/services?service=drone-filming')}>
                    <FontAwesomeIcon icon={faArrowRight} /> Drone Filming
                  </button>
                </li>
                <li>
                  <button type="button" className="footer-link-btn" onClick={() => navigate('/services?service=post-production')}>
                    <FontAwesomeIcon icon={faArrowRight} /> Post Production
                  </button>
                </li>
              </ul>
            </div>

            {/* Contact Column */}
            <div className="col-lg-3 col-md-6 mb-4 mb-lg-0">
              <h5 className="footer-heading">Contact Info</h5>
              <ul className="footer-contact-info">
                <li>
                  <FontAwesomeIcon icon={faMapMarkerAlt} />
                  <a href="https://maps.app.goo.gl/8EWqWuqhjHFAVd2k7" target="_blank" rel="noopener noreferrer" className="footer-address-link">
                    The Video Company, 318, Hosa Road Bengaluru, Karnataka 560068, India
                  </a>
                </li>
                <li>
                  <FontAwesomeIcon icon={faPhone} />
                  <a href="tel:+917411611528" onClick={handleClick}>+91 7411611528</a>
                </li>
                <li>
                  <FontAwesomeIcon icon={faEnvelope} />
                  <a href="mailto:connect@thevideocompany.in" onClick={handleClick}>connect@thevideocompany.in</a>
                </li>
                <li>
                  <FontAwesomeIcon icon={faClock} />
                  <span>Mon-Fri: 9AM - 5PM</span>
                </li>
              </ul>
            </div>

            {/* Newsletter Column */}
            <div className="col-lg-2 col-md-6">
              <h5 className="footer-heading">Newsletter</h5>
              <p className="newsletter-description">Subscribe to our newsletter for updates.</p>
              <form className="newsletter-form" onSubmit={handleSubscribeSubmit}>
                <div className="form-group">
                  <input
                    type="email"
                    placeholder="Your Email"
                    value={subscribeEmail}
                    onChange={(e) => setSubscribeEmail(e.target.value)}
                  />
                </div>
                <button type="submit" className="subscribe-button">
                  Subscribe <FontAwesomeIcon icon={faArrowRight} />
                </button>
                {isAuthenticated ? (
                  <div style={{display: 'flex', gap: '8px', flexWrap: 'wrap'}}>
                    {isAdmin && (
                      <button
                        type="button"
                        className="admin-login-button go-dashboard"
                        onClick={() => navigate('/dashboard')}
                      >
                        Dashboard <FontAwesomeIcon icon={faArrowRight} />
                      </button>
                    )}
                    {isEmployee && (
                      <button
                        type="button"
                        className="admin-login-button go-dashboard"
                        onClick={() => navigate('/employee')}
                      >
                        Employee Portal <FontAwesomeIcon icon={faArrowRight} />
                      </button>
                    )}
                    <button
                      type="button"
                      className="admin-login-button signout-button"
                      onClick={handleSignOut}
                      title="Sign out"
                    >
                      Sign out
                    </button>
                  </div>
                ) : (
                  <button type="button" className="admin-login-button" onClick={() => navigate('/login')}>
                    Login <FontAwesomeIcon icon={faUser} />
                  </button>
                )}
              </form>
            </div>
          </div>

          <hr className="footer-divider" />

          <div className="row align-items-center">
            <div className="col-md-6 text-center text-md-start">
              <p className="copyright-text">
                Copyright &copy; {new Date().getFullYear()} The Video Company. All rights reserved.
              </p>
            </div>
            <div className="col-md-6 text-center text-md-end">
              <div className="footer-legal-links">
                <Link to="/privacy-policy" onClick={handleClick}>Privacy Policy</Link>
                <Link to="/terms-and-conditions" onClick={handleClick}>Terms of Service</Link>
                <Link to="#" onClick={handleClick}>Sitemap</Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;